import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { collection, addDoc, getDocs, query, limit, Timestamp, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../contexts/AuthContext';

const FirebaseConnectionTest: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);
  const [results, setResults] = useState<Array<{
    test: string;
    success: boolean;
    message: string;
    data?: any;
  }>>([]);
  const [testId, setTestId] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<any>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: 'Employee' as UserRole,
    department: 'HR'
  });
  const [repairSuccess, setRepairSuccess] = useState(false);
  const [repairError, setRepairError] = useState<string | null>(null);

  // Handle text field changes
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle select field changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  // Function to check if user profile exists
  const checkUserProfile = async () => {
    if (!currentUser) {
      setResults(prev => [...prev, {
        test: 'Check User Profile',
        success: false,
        message: 'No authenticated user found'
      }]);
      return;
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        setProfileData(data);
        
        // Pre-fill form with existing data
        if (data.firstName) setFormData(prev => ({ ...prev, firstName: data.firstName }));
        if (data.lastName) setFormData(prev => ({ ...prev, lastName: data.lastName }));
        if (data.role) setFormData(prev => ({ ...prev, role: data.role }));
        if (data.department) setFormData(prev => ({ ...prev, department: data.department }));
        
        setResults(prev => [...prev, {
          test: 'Check User Profile',
          success: true,
          message: `User profile found for ${currentUser.uid}`,
          data: data
        }]);
      } else {
        setResults(prev => [...prev, {
          test: 'Check User Profile',
          success: false,
          message: `No user profile found for ${currentUser.uid}`
        }]);
      }
    } catch (error: any) {
      setResults(prev => [...prev, {
        test: 'Check User Profile',
        success: false,
        message: `Error checking user profile: ${error.message}`,
        data: error
      }]);
    }
  };

  // Function to repair or create user profile
  const repairUserProfile = async () => {
    if (!currentUser) {
      setRepairError('No authenticated user found');
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setRepairError('First name and last name are required');
      return;
    }

    setRepairLoading(true);
    setRepairError(null);
    setRepairSuccess(false);

    try {
      // Create or update profile
      const profileData = {
        uid: currentUser.uid,
        email: currentUser.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        department: formData.department,
        updatedAt: new Date()
      };

      // If we're creating a new profile, add creation date
      if (!profileData) {
        Object.assign(profileData, { createdAt: new Date() });
      }

      // Write to Firestore with merge option to update existing or create new
      await setDoc(doc(db, 'users', currentUser.uid), profileData, { merge: true });
      
      setRepairSuccess(true);
      setResults(prev => [...prev, {
        test: 'Repair User Profile',
        success: true,
        message: `Successfully ${profileData ? 'updated' : 'created'} user profile`,
        data: profileData
      }]);

      // Refresh profile data
      await checkUserProfile();

      // Force reload after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      console.error('Error repairing profile:', error);
      setRepairError(error.message || 'Failed to repair profile');
      setResults(prev => [...prev, {
        test: 'Repair User Profile',
        success: false,
        message: `Error repairing profile: ${error.message}`,
        data: error
      }]);
    } finally {
      setRepairLoading(false);
    }
  };

  const runTests = async () => {
    setLoading(true);
    setResults([]);
    
    // Test 1: Write to Firestore
    try {
      const testDoc = {
        testId: `connection-test-${Date.now()}`,
        timestamp: Timestamp.now(),
        userId: currentUser?.uid || 'anonymous',
        testData: { foo: 'bar' }
      };

      const writeResult = await addDoc(collection(db, 'diagnostics'), testDoc);
      setTestId(writeResult.id);
      
      setResults(prev => [...prev, {
        test: 'Write to Firestore',
        success: true,
        message: `Successfully wrote document with ID: ${writeResult.id}`,
        data: testDoc
      }]);
    } catch (error: any) {
      setResults(prev => [...prev, {
        test: 'Write to Firestore',
        success: false,
        message: `Error writing to Firestore: ${error.message}`,
        data: error
      }]);
    }

    // Test 2: Read from Firestore
    try {
      const querySnapshot = await getDocs(query(collection(db, 'diagnostics'), limit(5)));
      
      const docs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setResults(prev => [...prev, {
        test: 'Read from Firestore',
        success: true,
        message: `Successfully read ${querySnapshot.size} documents`,
        data: docs
      }]);
    } catch (error: any) {
      setResults(prev => [...prev, {
        test: 'Read from Firestore',
        success: false,
        message: `Error reading from Firestore: ${error.message}`,
        data: error
      }]);
    }

    // Test 3: Attempt to read user profile collection
    try {
      const querySnapshot = await getDocs(query(collection(db, 'users'), limit(1)));
      
      setResults(prev => [...prev, {
        test: 'Access Users Collection',
        success: true,
        message: `Successfully accessed users collection (${querySnapshot.size} documents)`,
        data: querySnapshot.size > 0 ? 'Collection exists and is accessible' : 'Collection exists but may be empty'
      }]);
    } catch (error: any) {
      setResults(prev => [...prev, {
        test: 'Access Users Collection',
        success: false,
        message: `Error accessing users collection: ${error.message}`,
        data: error
      }]);
    }

    // Test 4: Check current user's profile
    await checkUserProfile();

    // Test 5: Clean up test document
    if (testId) {
      try {
        await deleteDoc(doc(db, 'diagnostics', testId));
        
        setResults(prev => [...prev, {
          test: 'Clean Up Test Document',
          success: true,
          message: `Successfully deleted test document`,
        }]);
      } catch (error: any) {
        setResults(prev => [...prev, {
          test: 'Clean Up Test Document',
          success: false,
          message: `Error deleting test document: ${error.message}`,
          data: error
        }]);
      }
    }

    setLoading(false);
  };

  const roles = [
    'Administrator',
    'Manager',
    'HR',
    'Employee',
    'Accountant'
  ];

  const departments = [
    'Engineering',
    'Product',
    'Design',
    'Marketing',
    'Sales',
    'HR',
    'Finance',
    'Operations',
  ];

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>Firebase Connection Test</Typography>
        <Typography color="text.secondary" paragraph>
          Run basic tests to verify your connection to Firebase Firestore.
        </Typography>

        <Paper sx={{ p: 3, my: 3 }}>
          <Typography variant="h6" gutterBottom>Firebase Connection Tests</Typography>
          <Typography paragraph>
            This will test basic read and write operations with Firestore to help diagnose connection issues.
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            onClick={runTests}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ mb: 3 }}
          >
            {loading ? 'Running Tests...' : 'Run Connection Tests'}
          </Button>

          {results.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Test Results</Typography>
              
              <List>
                {results.map((result, index) => (
                  <ListItem 
                    key={index}
                    divider={index < results.length - 1}
                    sx={{ 
                      bgcolor: result.success ? 'success.light' : 'error.light',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{result.test}</Typography>
                          <Chip 
                            size="small" 
                            label={result.success ? 'Success' : 'Failed'} 
                            color={result.success ? 'success' : 'error'} 
                          />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2">{result.message}</Typography>
                          {result.data && (
                            <Box
                              component="pre"
                              sx={{
                                mt: 1,
                                p: 1,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                maxHeight: 150,
                                overflow: 'auto'
                              }}
                            >
                              {typeof result.data === 'string' 
                                ? result.data 
                                : JSON.stringify(result.data, null, 2)
                              }
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              
              {results.some(r => !r.success) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Some tests failed. This may indicate issues with your Firebase configuration or permissions.
                </Alert>
              )}
              
              {results.every(r => r.success) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  All tests passed! Your Firebase connection appears to be working correctly.
                </Alert>
              )}
            </>
          )}
        </Paper>

        {currentUser && (
          <Paper sx={{ p: 3, my: 3 }}>
            <Typography variant="h6" gutterBottom>Profile Repair Tool</Typography>
            <Typography paragraph>
              If your profile isn't loading correctly, you can use this tool to repair or create it.
            </Typography>

            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleTextChange}
                required
                fullWidth
              />

              <TextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleTextChange}
                required
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleSelectChange}
                  label="Role"
                >
                  {roles.map(role => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleSelectChange}
                  label="Department"
                >
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {repairError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {repairError}
                </Alert>
              )}

              {repairSuccess && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Profile repaired successfully! Reloading...
                </Alert>
              )}

              <Button
                variant="contained"
                color="warning"
                onClick={repairUserProfile}
                disabled={repairLoading || !formData.firstName || !formData.lastName}
                startIcon={repairLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {repairLoading ? 'Repairing...' : profileData ? 'Update Profile' : 'Create Profile'}
              </Button>
            </Box>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => window.location.href = '/profile'}
          >
            Back to Profile
          </Button>
          <Button
            variant="outlined"
            onClick={() => window.location.href = '/auth-debug'}
          >
            Go to Auth Debugger
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default FirebaseConnectionTest; 